/**
 * 계정 관리 — 콘솔 목록 공통 규격 채택본 (2026-09-09 사용자 지시).
 *
 * 분류축이 없으므로 **형 A**다. 이 화면의 본체 작업은 목록이 아니라 **편집 자리**였다.
 *
 * ★★ 이전 화면은 **표 안에서 한 줄이 열리는 인라인 편집**이었다(`cacc__editrow`,
 *    `<td colSpan={5}>` 안에 폼). 정본 P3-1 이 「표 안에서 한 줄이 열리는 인라인 편집」을
 *    하지 않는다고 못 박아 둔 바로 그 모양이라, 팝업으로 옮긴다. 규칙 ⑤ 와도 같다.
 *
 * ★ 잃는 기능은 없다 — 등록·수정·사용해제·명단에서 빼기, 오류 문구 표(ERR_MAP),
 *   비밀번호 보기 토글, 「회사 직원 목록에도 보이기」까지 그대로 옮겼다.
 *
 * ⚠ 이전 화면 `ConsoleAccountSection` 은 되돌리기 대비로 남겨 둔다(그 파일의 CSS
 *   `cacc__*` 도 그대로 둔다 — 이 화면은 공통 `console-list.css` 만 쓴다).
 * ⚠ 서버 변경 0 — 부르는 라우트가 이전과 똑같다.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, CheckCircle, Eye, EyeOff, Loader2, Pencil, ShieldCheck, ShieldOff, Smartphone, Trash2, X, XCircle } from '../../../icons';
import {
  createConsoleAccount, listConsoleAccounts, removeConsoleAccount, resetConsoleAccountMfa, setConsoleAccountMfa,
  updateConsoleAccount,
  type ConsoleAccount, type ConsoleRole,
} from '../../../api/consoleAccounts';
import { useAdminAuthStore } from '../../../store/adminAuthStore';
import { assignableRolesFor } from '../../../lib/roleRank';
import { ConsoleListScreen } from '../../../components/console-list/ConsoleListScreen';
import { ConsoleListTable } from '../../../components/console-list/ConsoleListTable';

const ROLE_LABEL: Record<string, string> = {
  SYSTEM_ADMIN: '시스템관리자', ADMIN: '관리자', MANAGER: '매니저', STAFF: '직원', AI_BOT: '봇',
};
/** BFF `config.allowedRoles` 와 같은 값이어야 한다 — 아니면 「등록은 됐는데 로그인은 안 되는」 계정이 생긴다. */
const CONSOLE_ROLES: ConsoleRole[] = ['SYSTEM_ADMIN'];

const ERR_MAP: Record<string, string> = {
  LAST_CONSOLE_ACCOUNT: '마지막 계정입니다. 다른 계정을 먼저 등록하세요.',
  NOT_REGISTERED: '명단에 없는 계정입니다.',
  NAME_REQUIRED: '이름을 입력하세요.',
  EMAIL_REQUIRED: '이메일을 입력하세요.',
  PASSWORD_REQUIRED: '비밀번호를 입력하세요.',
  ROLE_INVALID: '콘솔 계정은 시스템관리자만 됩니다.',
  EMAIL_INVALID: '이메일 형식이 올바르지 않습니다.',
  EMAIL_DOMAIN_MISMATCH: '회사 도메인의 이메일만 쓸 수 있습니다.',
  EMAIL_EXISTS: '이미 사용 중인 이메일입니다.',
  DELETED_EMAIL_EXISTS: '이전에 탈퇴한 계정이 같은 이메일을 쓰고 있습니다.',
  EMPLOYEE_NUMBER_EXISTS: '이미 사용 중인 사번입니다.',
  PASSWORD_POLICY_VIOLATED: '비밀번호 정책 위반 — 8자 이상, 영문·숫자·특수문자 3종 이상.',
  ROLE_ESCALATION_DENIED: '자신보다 상위 권한은 부여할 수 없습니다.',
  CANNOT_MANAGE_HIGHER_ROLE: '자신보다 상위 권한 계정은 고칠 수 없습니다.',
  CANNOT_CHANGE_HIGHER_ROLE_PASSWORD: '자신보다 상위 권한 계정의 비밀번호는 바꿀 수 없습니다.',
  CANNOT_CHANGE_OWN_PASSWORD: '본인 비밀번호는 내 계정 설정에서 바꾸세요.',
  CANNOT_CHANGE_OWN_ROLE: '본인의 권한은 바꿀 수 없습니다.',
  CANNOT_CHANGE_ADMIN_EMAIL: '다른 관리자 계정의 이메일은 바꿀 수 없습니다.',
  SEATS_EXCEEDED: '사용자 수 상한에 도달했습니다.',
};
function errMessage(e: unknown, fallback: string): string {
  const d = (e as { response?: { data?: { error?: string; message?: string; violations?: string[] } } })?.response?.data;
  const mapped = d?.error ? ERR_MAP[d.error] : undefined;
  const v = Array.isArray(d?.violations) && d.violations.length ? ` (${d.violations.join(', ')})` : '';
  return `${mapped ?? d?.message ?? fallback}${v}`;
}
function fmtDate(v: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('ko-KR');
}

interface FormState {
  name: string; email: string; role: ConsoleRole; password: string; employeeNumber: string; note: string;
  /** 회사 직원 목록(8443)에 보일지. 콘솔 계정은 보통 운영용이라 기본은 감춤이다. */
  showInDirectory: boolean;
}
const EMPTY_FORM: FormState = { name: '', email: '', role: 'ADMIN', password: '', employeeNumber: '', note: '', showInDirectory: false };

export function ConsoleAccountListSection() {
  const me = useAdminAuthStore((s) => s.user);
  const myRole = me?.role ?? '';
  const [items, setItems] = useState<ConsoleAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [only, setOnly] = useState<'all' | 'on'>('all');

  /** 'new' = 등록 팝업, ConsoleAccount = 그 계정 수정 팝업, null = 닫힘. */
  const [open, setOpen] = useState<'new' | ConsoleAccount | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [showPw, setShowPw] = useState(false);

  const roleOptions = useMemo(() => {
    const mine = new Set(assignableRolesFor(myRole));
    return CONSOLE_ROLES.filter((r) => mine.has(r));
  }, [myRole]);

  const reload = useCallback(async () => {
    setLoading(true);
    try { const r = await listConsoleAccounts(); setItems(r.items); setError(null); }
    catch (e) { setError(errMessage(e, '명단을 불러오지 못했습니다.')); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  const activeCount = useMemo(() => items.filter((i) => i.is_active).length, [items]);
  /**
   * 콘솔 계정이 사람 계정과 분리된 상태인가 (2026-10-15 사용자 결정 「콘솔 계정과 인사 계정은 다르다」).
   * 서버가 콘솔 자체 인증이면 목록에 `mfa_enrolled` 를 싣는다 ⇒ 사번·직원 목록 칸은 뜻이 없어 감추고,
   * 2차 인증 상태와 초기화를 보인다. 종전 서버면 이 칸이 없어 화면이 예전 그대로다.
   */
  const standalone = useMemo(() => items.some((i) => typeof i.mfa_enrolled === 'boolean'), [items]);
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((a) => {
      if (only === 'on' && !a.is_active) return false;
      if (!needle) return true;
      return `${a.user_name ?? a.name} ${a.user_email ?? a.email} ${a.note ?? ''}`.toLowerCase().includes(needle);
    });
  }, [items, q, only]);

  function openCreate() {
    setForm({ ...EMPTY_FORM, role: roleOptions.includes('ADMIN') ? 'ADMIN' : (roleOptions[0] ?? 'ADMIN') });
    setShowPw(false); setError(null); setNotice(null); setOpen('new');
  }
  function openEdit(a: ConsoleAccount) {
    setForm({
      name: a.user_name ?? a.name,
      email: a.user_email ?? a.email,
      role: (a.console_role === 'ADMIN' ? 'ADMIN' : 'SYSTEM_ADMIN'),
      password: '', employeeNumber: '',
      showInDirectory: !a.is_service_account,
      note: a.note ?? '',
    });
    setShowPw(false); setError(null); setNotice(null); setOpen(a);
  }

  async function run(key: string, fn: () => Promise<void>, okMsg: string, close = true) {
    setBusy(key); setError(null); setNotice(null);
    try {
      await fn();
      setNotice(okMsg);
      if (close) setOpen(null);
      await reload();
    } catch (e) { setError(errMessage(e, '처리하지 못했습니다.')); }
    finally { setBusy(null); }
  }

  function submitCreate() {
    void run('new', async () => {
      await createConsoleAccount({
        name: form.name.trim(), email: form.email.trim(), role: form.role, password: form.password,
        ...(!standalone && form.employeeNumber.trim() ? { employeeNumber: form.employeeNumber.trim() } : {}),
        ...(!standalone ? { showInDirectory: form.showInDirectory } : {}),
        ...(form.note.trim() ? { note: form.note.trim() } : {}),
      });
    }, `${form.name.trim()} 계정을 만들고 명단에 올렸습니다.`);
  }
  function submitEdit(a: ConsoleAccount) {
    const patch: Record<string, unknown> = {};
    const curName = a.user_name ?? a.name;
    const curEmail = a.user_email ?? a.email;
    if (form.name.trim() && form.name.trim() !== curName) patch.name = form.name.trim();
    if (form.email.trim() && form.email.trim() !== curEmail) patch.email = form.email.trim();
    if (form.role !== a.console_role) patch.role = form.role;
    if (form.password) patch.password = form.password;
    if (form.note.trim() !== (a.note ?? '')) patch.note = form.note.trim();
    if (!standalone && form.showInDirectory !== !a.is_service_account) patch.showInDirectory = form.showInDirectory;
    if (Object.keys(patch).length === 0) { setNotice('바뀐 내용이 없습니다.'); setOpen(null); return; }
    void run(a.user_id, () => updateConsoleAccount(a.user_id, patch), `${curName} 님의 계정을 저장했습니다.`);
  }

  const createValid = form.name.trim() !== '' && form.email.trim() !== '' && form.password !== '';
  const isNew = open === 'new';
  const editingRow = open && open !== 'new' ? open : null;

  return (
    <>
      <ConsoleListScreen
        title="계정 관리"
        kpi={`사용 ${activeCount}개 · 전체 ${items.length}개`}
        desc="관리 콘솔에 들어올 수 있는 계정입니다. 여기에 없으면 로그인되지 않습니다. 「명단에서 빼기」는 콘솔 열쇠만 회수하고 계정 자체는 남습니다 — 마지막 한 명은 뺄 수 없습니다."
        search={{ value: q, onChange: setQ, placeholder: '이름 · 이메일 · 메모로 찾기' }}
        filters={{
          active: only,
          onChange: (k) => setOnly(k as 'all' | 'on'),
          items: [
            { key: 'all', label: '전체', count: items.length },
            { key: 'on', label: '사용 중', count: activeCount },
          ],
        }}
        add={{ label: '계정 등록', onClick: openCreate }}
        notice={
          <>
            {error && <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div>}
            {notice && <div className="admin-console__alert" role="status">{notice}</div>}
          </>
        }
      >
        {loading ? (
          <div className="cl-empty"><Loader2 size={16} /> 불러오는 중…</div>
        ) : (
          <ConsoleListTable
            rows={shown}
            rowKey={(a) => a.user_id}
            onRowClick={(a) => { if (!a.user_deleted) openEdit(a); }}
            empty={items.length === 0
              ? <>등록된 계정이 없습니다.<span className="cl-empty__sub">이 상태에서는 관문이 열려 지금까지처럼 역할만으로 들어옵니다.</span></>
              : <>찾는 계정이 없습니다.<span className="cl-empty__sub">검색어나 필터를 지워 보세요.</span></>}
            columns={[
              {
                key: 'who', header: '계정',
                render: (a) => (
                  <span className="cl-name">
                    <span className="cl-name__t">{a.user_name ?? a.name}{me?.id === a.user_id && ' (나)'}</span>
                    <span className="cl-name__s">{a.user_email ?? a.email}{a.note ? ` · ${a.note}` : ''}</span>
                  </span>
                ),
              },
              {
                key: 'role', header: '권한',
                render: (a) => {
                  const role = a.console_role;
                  const noPower = !a.user_deleted && role !== 'ADMIN' && role !== 'SYSTEM_ADMIN';
                  if (a.user_deleted) return <span className="cl-badge">계정 없음</span>;
                  return <span className="cl-badge">{ROLE_LABEL[role ?? ''] ?? role ?? '—'}{noPower && ' · 못 들어옴'}</span>;
                },
              },
              {
                key: 'state', header: '상태',
                render: (a) => (
                  <>
                    <span className={`cl-badge${a.is_active ? ' is-on' : ''}`}>{a.is_active ? '사용' : '해제됨'}</span>
                    {!a.password_set && !a.user_deleted && <> <span className="cl-badge">비밀번호 미설정</span></>}
                    {!standalone && a.is_service_account && <> <span className="cl-badge">직원 목록에서 감춤</span></>}
                    {standalone && <> <span className={`cl-badge${a.mfa_enrolled ? ' is-on' : ''}`}>
                      {a.mfa_enrolled ? '2단계 인증' : a.mfa_disabled ? '2단계 인증 꺼짐' : '2단계 인증 미등록'}
                    </span></>}
                  </>
                ),
              },
              { key: 'last', header: '마지막 로그인', render: (a) => fmtDate(a.last_login_at) },
            ]}
            /* 줄 끝 작업은 **아이콘만**. 켜짐·꺼짐은 「상태」 칸이 이미 말하므로,
             * 여기 아이콘은 «누르면 무엇이 되는지»를 그린다. */
            rowActions={(a) => (
              <>
                <button
                  type="button" className="cl-rowbtn"
                  disabled={busy === a.user_id || a.user_deleted}
                  aria-label={`${a.user_name ?? a.name} 수정`} title="수정"
                  onClick={() => openEdit(a)}
                >
                  <Pencil size={13} />
                </button>
                <button
                  type="button" className="cl-rowbtn" disabled={busy === a.user_id}
                  aria-label={a.is_active ? '사용 해제' : '사용'}
                  title={a.is_active ? '사용 해제' : '사용'}
                  onClick={() => void run(
                    a.user_id,
                    () => updateConsoleAccount(a.user_id, { isActive: !a.is_active }),
                    a.is_active ? '사용을 해제했습니다.' : '다시 사용하도록 바꿨습니다.',
                  )}
                >
                  {a.is_active ? <XCircle size={13} /> : <CheckCircle size={13} />}
                </button>
                {/* 2단계 인증 끄기·켜기 (2026-10-15 사용자 요청) — 본인 것은 다른 관리자가 바꾼다. */}
                {standalone && me?.id !== a.user_id && (
                  <button
                    type="button" className="cl-rowbtn" disabled={busy === a.user_id}
                    aria-label={`${a.user_name ?? a.name} 2단계 인증 ${a.mfa_disabled ? '켜기' : '끄기'}`}
                    title={a.mfa_disabled ? '2단계 인증 켜기' : '2단계 인증 끄기'}
                    onClick={() => void run(
                      a.user_id,
                      () => setConsoleAccountMfa(a.user_id, Boolean(a.mfa_disabled)),
                      a.mfa_disabled
                        ? '2단계 인증을 켰습니다. 그 계정은 다음 로그인에서 QR 등록부터 합니다.'
                        : '2단계 인증을 껐습니다. 다음 로그인부터 QR·번호를 묻지 않습니다.',
                    )}
                  >
                    {a.mfa_disabled ? <ShieldCheck size={13} /> : <ShieldOff size={13} />}
                  </button>
                )}
                {standalone && a.mfa_enrolled && me?.id !== a.user_id && (
                  <button
                    type="button" className="cl-rowbtn" disabled={busy === a.user_id}
                    aria-label={`${a.user_name ?? a.name} 2단계 인증 초기화`} title="2단계 인증 초기화"
                    onClick={() => void run(a.user_id, () => resetConsoleAccountMfa(a.user_id),
                      '2단계 인증을 초기화했습니다. 다음 로그인에서 다시 등록합니다.')}
                  >
                    <Smartphone size={13} />
                  </button>
                )}
                <button
                  type="button" className="cl-rowbtn cl-rowbtn--danger" disabled={busy === a.user_id}
                  aria-label={`${a.user_name ?? a.name} 명단에서 빼기`} title="명단에서 빼기"
                  onClick={() => void run(a.user_id, () => removeConsoleAccount(a.user_id), '명단에서 뺐습니다.')}
                >
                  <Trash2 size={13} />
                </button>
              </>
            )}
          />
        )}
      </ConsoleListScreen>

      {open && (
        <div className="cl-modal__backdrop" role="presentation" onClick={() => setOpen(null)}>
          <div
            className="cl-modal" role="dialog" aria-modal="true"
            aria-label={isNew ? '계정 등록' : '계정 수정'}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cl-modal__head">
              <h3>{isNew ? '계정 등록' : '계정 수정'}</h3>
              <button type="button" className="cl-modal__close" aria-label="닫기" onClick={() => setOpen(null)}>
                <X size={16} />
              </button>
            </div>
            <div className="cl-modal__body">
              <div className="cl-field">
                <label htmlFor="cl-acc-name">이름<em>*</em></label>
                <input id="cl-acc-name" type="text" value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="cl-field">
                <label htmlFor="cl-acc-email">이메일<em>*</em></label>
                <input id="cl-acc-email" type="email" value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                <span className="cl-field__hint">
                  {standalone
                    ? '로그인 아이디입니다. 회사 직원 계정과는 별개라 같은 이메일이어도 됩니다.'
                    : '로그인 아이디입니다. 회사 도메인만 쓸 수 있습니다.'}
                </span>
              </div>
              <div className="cl-field">
                <label htmlFor="cl-acc-role">권한</label>
                <select id="cl-acc-role" value={form.role}
                  onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as ConsoleRole }))}>
                  {roleOptions.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                </select>
              </div>
              <div className="cl-field">
                <label htmlFor="cl-acc-pw">비밀번호{isNew && <em>*</em>}</label>
                <span className="cl-field__row">
                  <input id="cl-acc-pw" type={showPw ? 'text' : 'password'} value={form.password}
                    autoComplete="new-password"
                    placeholder={isNew ? '8자 이상 · 영문·숫자·특수문자 3종' : '바꿀 때만 입력'}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
                  <button type="button" className="cl-modal__btn" aria-label={showPw ? '숨기기' : '보기'}
                    onClick={() => setShowPw((v) => !v)}>
                    {showPw ? <EyeOff size={13} /> : <Eye size={13} />}
                  </button>
                </span>
              </div>
              {isNew && !standalone && (
                <div className="cl-field">
                  <label htmlFor="cl-acc-emp">사번</label>
                  <input id="cl-acc-emp" type="text" value={form.employeeNumber}
                    onChange={(e) => setForm((f) => ({ ...f, employeeNumber: e.target.value }))} />
                  <span className="cl-field__hint">선택. 사번 로그인·임시비번 발급에 쓰입니다.</span>
                </div>
              )}
              {!standalone && <div className="cl-field">
                <label className="cl-check" htmlFor="cl-acc-dir">
                  <input id="cl-acc-dir" type="checkbox" checked={form.showInDirectory}
                    onChange={(e) => setForm((f) => ({ ...f, showInDirectory: e.target.checked }))} />
                  <span>회사 직원 목록에도 보이기</span>
                </label>
                <span className="cl-field__hint">
                  끄면 이 계정은 8443 사용자 목록·조직도에 안 나옵니다. 실제로 근무하는 직원이
                  콘솔도 쓰는 경우에만 켜세요.
                </span>
              </div>}
              <div className="cl-field">
                <label htmlFor="cl-acc-note">메모</label>
                <input id="cl-acc-note" type="text" value={form.note}
                  onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
              </div>
              {error && <div className="cl-modal__alert" role="alert"><AlertTriangle size={14} /> {error}</div>}
            </div>
            <div className="cl-modal__foot">
              <button type="button" className="cl-modal__btn" onClick={() => setOpen(null)}>취소</button>
              <button
                type="button" className="cl-modal__btn cl-modal__btn--primary"
                disabled={busy !== null || (isNew && !createValid)}
                onClick={() => (isNew ? submitCreate() : editingRow && submitEdit(editingRow))}
              >
                {busy !== null ? '처리 중…' : (isNew ? <><Check size={13} /> 등록</> : '저장')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
